/**
 * High priority imports
 */

import {
	DebounceFunctionsStorage,
	ThrottleFunctionsStorage,
} from "@rightioltd/debounce-throttle-npm";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import Changelog from "./components/Shared/Changelog";
import { upgradeLocalStorage } from "./helpers";
import { state, ui } from "./interfaces/interfaces";
import { resetStateReducer } from "./reducers/stateReducer/operations";
import { resetTempReducer } from "./reducers/tempReducer/operations";
import { resetUIReducer } from "./reducers/uiReducer/operations";
import reportWebVitals from "./reportWebVitals";
import bookingCycleMgr from "./services/bookingCycleMgr";
import { FaroErrorBoundary, isMonitoringEnabled } from "./services/monitoring";
import {
	createBookingsSyncWorker,
	createCacheWorker,
	createLogSyncWorker,
	createWebSocketsWorker,
} from "./services/workers";
import { store } from "./store";
import AddressUtils from "./utilities/address";
import { AuthProvider } from "./utilities/authProvider";
import CacheUtils from "./utilities/cache";
import config from "./utilities/config";
import QuestionsUtils from "./utilities/questions";
import {
	createLoggerWorker,
	/* createWorkerPuzzel, */ createWorkerBookingsSync,
	createWorkerCache,
	createWorkerWebsocket,
} from "./workersFactory"; // createWorkerPuzzel unused

export const debounceStorage = new DebounceFunctionsStorage();
export const throttleStorage = new ThrottleFunctionsStorage();

/**
 * The base URL of the application.
 * @type {string}
 */
const baseUrl = document
	.getElementsByTagName("base")[0]
	.getAttribute("href") as string;

// Ensure the base URL exists
if (!baseUrl) {
	throw new Error("Base URL not found");
}

/**
 * The root DOM element where the React application will be mounted.
 * @type {HTMLElement|null}
 */
const container = document.getElementById("root");

/**
 * The root React element where the React application will be mounted.
 * @type {Root|null}
 */
const root = container ? createRoot(container) : null;

// Ensure the root element exists
if (!root) {
	throw new Error("Root element not found");
}

upgradeLocalStorage();
export const cacheUtils = new CacheUtils();

// initReactApp will prepare async IndexedDB for read/write, then combine reducers, create store and finally render APP in DOM
const initReactApp = async () => {
	bookingCycleMgr.load();
	await AddressUtils.createAddressTableInIndexedDB(); // <--- first call to prepare IndexedDB for ADDRESSES
	resetUIReducer(); //load UI store values from localStorage OR template
	resetTempReducer(); //load temp store values from indexedDB OR template

	resetStateReducer(); //load State store values from localStorage OR template

	state.CacheLoaded = true;
	state.LoadDate = new Date();

	const application = (
		<Provider store={store}>
			<Changelog />
			<AuthProvider>
				<BrowserRouter
					basename={baseUrl}
					future={{
						v7_startTransition: true,
						v7_relativeSplatPath: true,
					}}
				>
					<App />
				</BrowserRouter>
			</AuthProvider>
		</Provider>
	);

	isMonitoringEnabled
		? root.render(<FaroErrorBoundary>{application}</FaroErrorBoundary>)
		: root.render(application);

	// registerServiceWorker();
};

console.log("Running in:", config);

createLogSyncWorker(...createLoggerWorker("LOG_SYNC"));
createCacheWorker(...createWorkerCache(cacheUtils, "CACHE_SYNC"));
createBookingsSyncWorker(
	...createWorkerBookingsSync(state, "BOOKING_DATA_SYNC"),
);
createWebSocketsWorker(
	...createWorkerWebsocket({ state: state, ui: ui }, "WEBSOCKET_SYNC"),
);

initReactApp();
reportWebVitals(console.log);
// Uncomment the line above that imports the registerServiceWorker function
// and the line below to registerImport rootReducer from './reducer/rootReducer';
//  the generated service worker.import workers from "./utilities/workers/index";
// By default create-react-app includes a service worker to improve the
// performance of the application by caching static assets. This service
// worker can interfere with the Identity UI, so it is
// disabled by default when Identity is being used.
//
//registerServiceWorker();

// declare window variable
declare global {
	interface Window {
		CacheUtils: CacheUtils;
	}
}

// declare worker variable
declare global {
	interface Window {
		debounceStorage: DebounceFunctionsStorage;
		throttleStorage: ThrottleFunctionsStorage;
	}
}

window.CacheUtils = cacheUtils;
window.debounceStorage = debounceStorage;
window.throttleStorage = throttleStorage;

export const questionsUtils = new QuestionsUtils();
